import { getAccessToken } from './auth-cookie';

/** Ends the session slightly early, so clock drift never sends a token the server already rejects. */
export const SESSION_EXPIRY_MARGIN_MS = 30_000;

/**
 * Reads `exp` from a JWT access token, in milliseconds. Okta sessions store
 * `access_token:id_token`; only the access token's claim counts. Null when the token
 * carries no readable expiry, in which case only the server can tell.
 */
export function getAccessTokenExpiry(token: string): number | null {
  const [accessToken] = token.split(':');
  const payload = accessToken.split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: unknown;
    };
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    // Never log the error: parse messages quote the input, which is part of the token.
    return null;
  }
}

/**
 * Milliseconds left in the current session: 0 once it is over (no token, or past `exp`),
 * null when the token has no readable expiry.
 */
export function getSessionTimeLeft(now = Date.now()): number | null {
  const token = getAccessToken();

  if (!token) {
    return 0;
  }

  const expiry = getAccessTokenExpiry(token);
  if (expiry === null) {
    return null;
  }

  return Math.max(0, expiry - SESSION_EXPIRY_MARGIN_MS - now);
}
